import AddIcon from '@mui/icons-material/Add';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridFilterItem, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { RefreshIcon } from 'assets/icons';
import MasterFilter, { type DateRangeValue, useMasterFilter } from 'components/common/MasterFilter';
import SmartFilterModal from 'components/common/MasterFilter/SmartFilterModal';
import { gridItemsToActiveFilters } from 'components/common/MasterFilter/smartFilterMapping';
import { useAssignableUsers } from 'components/common/MasterFilter/useAssignableUsers';
import { useLeadFilterOptions } from 'components/common/MasterFilter/useLeadFilterOptions';
import { PrimaryButton } from 'components/forms';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSidebar } from 'contexts/SidebarContext';
import { onMessage } from 'firebase/messaging';
import useCurrentUser from 'hooks/useCurrentUser';
import type { Moment } from 'moment';
import moment from 'moment';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import { type ClientCountBucket, useGetClientsQuery } from 'services/api/clients.api';
import type { Client } from 'services/types/client';
import { MOBILE_PAGE_PT, NAVY, SURFACE } from 'styles/tokens';
import { SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID, SALFA_RAC_EXT_BUSINESS_UNIT_ID, SALFA_USED_ID } from 'utils/constants';
import { subscribeChannel } from 'utils/pusherClient';

import { messaging } from 'firebaseConfig';
import clientsBarConfig from './clientsBarConfig';
import clientsFilterFields from './clientsFilterFields';
import ClientList from './components/ClientList';
import ClientQueueTable, { ClientQueueTab } from './components/ClientQueueTable';
import NewLeadModal from './components/NewLeadModal';
import SortDropdown from './components/SortDropdown';

export type NewMessageEvent = {
  messageId: number;
  clientId: number;
  messagePreview?: string;
  userId?: number;
};

interface PaginationModel {
  page: number;
  pageSize: number;
}

interface ClientsQueryParams {
  paginationModel: PaginationModel;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  searchTerm?: string;
  businessUnitId?: number;
  businessUnitType?: 'car_dealership' | 'financial_institution' | 'insurance_broker' | null;
  countBuckets?: ClientCountBucket[];
}

interface Props {
  clients: Client[];
  isLoading: boolean;
  refetch: () => void;
  filterModel: GridFilterModel;
  paginationModel: PaginationModel;
  sortModel: GridSortModel;
  setFilterModel: (model: GridFilterModel) => void;
  setPaginationModel: (model: PaginationModel) => void;
  setSortModel: (model: GridSortModel) => void;
  navigateToClientId: (id: number, newPage?: boolean) => void;
  total: number;
  highlightedClientId: string;
  businessUnitType: string;
  newEvents: NewMessageEvent[];
  startDate: Moment | null;
  endDate: Moment | null;
  clientQueueSelectedTab: ClientQueueTab;
  setClientQueueSelectedTab: (tab: ClientQueueTab) => void;
  setTabFilterItems: (items: GridFilterItem[]) => void;
  /** Receives the tab-specific count buckets from the queue table; the parent composes and queries them. */
  setCountBuckets: (buckets: ClientCountBucket[]) => void;
  /** Tab badge totals, resolved from the single list query's `counts`. */
  tabCounts: Record<string, number>;
}

const ClientsTable: FC<Props> = ({
  clients,
  isLoading,
  refetch,
  paginationModel,
  sortModel,
  setPaginationModel,
  setSortModel,
  navigateToClientId,
  highlightedClientId,
  total,
  businessUnitType,
  newEvents,
  clientQueueSelectedTab,
  setClientQueueSelectedTab,
  setTabFilterItems,
  setCountBuckets,
  tabCounts,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const queryBusinessUnitId = queryParams.get('businessUnitId');
  const currentUser = useCurrentUser();

  const parsedBusinessUnitId = queryBusinessUnitId ? Number(queryBusinessUnitId) : 0;
  const { data: businessUnitData } = useGetBusinessUnitQuery(parsedBusinessUnitId, {
    skip: !parsedBusinessUnitId || parsedBusinessUnitId <= 0,
  });

  // Manual lead creation: the dedicated airport flow lives in its own BusinessUnit (Salfa RAC
  // Aeropuerto, BU 188). Only shown while the active business unit is that BU, to its members and admins.
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const isViewingRacAeropuerto = parsedBusinessUnitId === SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID;
  const isRacAeropuertoMember = !!currentUser?.businessUnitIds?.includes(SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID);
  const showNewLeadButton = isViewingRacAeropuerto && (isRacAeropuertoMember || !!currentUser?.isAdmin);

  const showClientQueueTable = !!businessUnitData?.hasSellerManagement && !!businessUnitData?.hasClientQueueTableActive;

  const refreshLabel =
    newEvents.length > 0
      ? `Actualizar lista — ${newEvents.length} cliente${newEvents.length === 1 ? '' : 's'} con actualizaciones pendientes`
      : 'Actualizar lista';

  const refreshButton = (
    <Tooltip title={refreshLabel} arrow>
      <Badge
        badgeContent={newEvents.length || undefined}
        max={99}
        sx={{
          '& .MuiBadge-badge': {
            bgcolor: NAVY,
            color: '#fff',
            fontSize: '0.7rem',
            minWidth: 18,
            height: 18,
            padding: '0 4px',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9px',
          },
        }}
      >
        <IconButton
          onClick={isLoading ? undefined : refetch}
          disabled={isLoading}
          size="small"
          aria-label={refreshLabel}
          sx={{
            color: alpha(NAVY, 0.55),
            transition: 'all 0.15s ease-in-out',
            '&:hover': { bgcolor: alpha(NAVY, 0.06) },
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Badge>
    </Tooltip>
  );

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tab bar row + sort + refresh */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, md: 2 }, mb: 0.5 }}
      >
        {showClientQueueTable ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <ClientQueueTable
              businessUnitId={businessUnitData.id}
              selectedTab={clientQueueSelectedTab}
              onSelectedTabChange={setClientQueueSelectedTab}
              onTabFilterChange={setTabFilterItems}
              onCountBucketsChange={setCountBuckets}
              tabCounts={tabCounts}
            />
            {refreshButton}
          </Box>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {showNewLeadButton && (
            <PrimaryButton
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setNewLeadOpen(true)}
              sx={{
                px: 2.5,
                py: 0.75,
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { boxShadow: 'none', transform: 'none' },
              }}
            >
              Nueva Conversación
            </PrimaryButton>
          )}
          {!showClientQueueTable && refreshButton}
          <Typography
            sx={{
              display: { xs: 'none', md: 'block' },
              fontSize: '0.8rem',
              color: `${NAVY}66`,
              fontFamily: 'Jost, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            Ordenar por:
          </Typography>
          <SortDropdown sortModel={sortModel} onSortModelChange={setSortModel} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ClientList
          clients={clients}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowCount={total}
          navigateToClientId={navigateToClientId}
          highlightedClientId={highlightedClientId}
          businessUnitType={businessUnitType}
          isAdmin={currentUser?.isAdmin ?? false}
          isLoading={isLoading}
        />
      </Box>
      {showNewLeadButton && (
        <NewLeadModal
          open={newLeadOpen}
          onClose={() => setNewLeadOpen(false)}
          businessUnitId={SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID}
          onSuccess={refetch}
        />
      )}
    </Box>
  );
};

const Clients: FC = () => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { selectedBusinessUnitId } = useSidebar();
  const { businessUnits } = useBusinessUnits();
  const queryParams = new URLSearchParams(window.location.search);
  const queryFilterModel = queryParams.get('filterModel');
  const queryPaginationModel = queryParams.get('paginationModel');
  const querySortModel = queryParams.get('sortModel');
  const querySearchTerm = queryParams.get('searchTerm');
  const queryBusinessUnitId = queryParams.get('businessUnitId');
  const highlightedClientId = queryParams.get('highlightedClientId') || '';

  const [newEvents, setNewEvents] = useState<NewMessageEvent[]>([]);
  const parsedBusinessUnitId = parseInt(queryBusinessUnitId || '', 10);

  const getInitialBusinessUnitId = (): number | null => {
    // If URL has valid BU param, use it
    if (!Number.isNaN(parsedBusinessUnitId)) return parsedBusinessUnitId;
    // Otherwise, use sidebar selection if it's a specific BU (> 0)
    if (selectedBusinessUnitId && selectedBusinessUnitId > 0) return selectedBusinessUnitId;
    // Default to null if no valid selection
    return null;
  };

  const [businessUnitId, setBusinessUnitId] = useState<number | null>(getInitialBusinessUnitId());

  // ── MasterFilter bar (search, date range, sources, topics) ──
  const filter = useMasterFilter(clientsBarConfig, businessUnitId);
  const searchTerm = (filter.values.searchTerm as string) ?? '';
  const businessUnitTypeFilter: 'car_dealership' | 'financial_institution' | 'insurance_broker' | null = null;

  // Stabilize array/date references so downstream useMemos don't re-run on every render
  const selectedSources = useMemo(() => (filter.values.sources as string[] | undefined) ?? [], [filter.values.sources]);
  const selectedTopics = useMemo(() => (filter.values.topics as string[] | undefined) ?? [], [filter.values.topics]);
  const startDate = useMemo(
    () => (filter.values.dateRange as DateRangeValue | undefined)?.start ?? null,
    [filter.values.dateRange]
  );
  const endDate = useMemo(
    () => (filter.values.dateRange as DateRangeValue | undefined)?.end ?? null,
    [filter.values.dateRange]
  );

  // Build filter items from bar multiSelect values
  const barFilterItems: GridFilterItem[] = useMemo(() => {
    const items: GridFilterItem[] = [];
    if (selectedSources.length > 0) {
      items.push({ field: 'source', operator: 'isAnyOf', value: selectedSources });
    }
    if (selectedTopics.length > 0) {
      items.push({ field: 'topics', operator: 'isAnyOf', value: selectedTopics });
    }
    if (startDate) {
      items.push({ field: 'lastMessageDate', operator: 'greaterThan', value: startDate.toISOString() });
    }
    if (endDate) {
      items.push({ field: 'lastMessageDate', operator: 'lessThan', value: endDate.toISOString() });
    }
    return items;
  }, [selectedSources, selectedTopics, startDate, endDate]);

  // ── SmartFilterModal (advanced filters) ──
  const querySelectedSources = queryParams.get('selectedSources');
  const querySelectedTopics = queryParams.get('selectedTopics');
  const queryStartDate = queryParams.get('startDate');
  const queryEndDate = queryParams.get('endDate');
  const querySmartFilterItems = queryParams.get('smartFilterItems');
  const [smartFilterItems, setSmartFilterItems] = useState<GridFilterItem[]>(
    querySmartFilterItems ? (JSON.parse(querySmartFilterItems) as GridFilterItem[]) : []
  );
  const [isSmartFilterOpen, setSmartFilterOpen] = useState(false);

  // ── Dynamic options for MasterFilter + SmartFilterModal ──
  // Modal-only options are deferred until the SmartFilterModal opens (or until a chip
  // for one of those fields is already active and needs its label resolved).
  // The Clients modal never uses campaigns/origins, so skipCampaigns stays true.
  const hasBrandsFilter = smartFilterItems.some((i) => i.field === 'brandOfInterest');
  const hasBranchOfficeFilter = smartFilterItems.some((i) => i.field === 'branchOffice');
  const hasAssignedUserFilter = smartFilterItems.some((i) => i.field === 'clientUserId');
  const assignableUsers = useAssignableUsers(businessUnitId, {
    skip: !isSmartFilterOpen && !hasAssignedUserFilter,
  });
  const leadFilterOptions = useLeadFilterOptions(businessUnitId, null, null, 'client', {
    skipBrands: !isSmartFilterOpen && !hasBrandsFilter,
    skipBranchOffices: !isSmartFilterOpen && !hasBranchOfficeFilter,
    skipCampaigns: true,
  });
  const dynamicOptions = useMemo(
    () => ({ ...leadFilterOptions, assignedUser: assignableUsers }),
    [leadFilterOptions, assignableUsers]
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    queryFilterModel ? (JSON.parse(queryFilterModel) as GridFilterModel) : { items: [] }
  );
  const parsedQueryPaginationModel = queryPaginationModel
    ? (JSON.parse(queryPaginationModel) as PaginationModel | null)
    : null;
  const [paginationModel, setPaginationModel] = useState<PaginationModel>(
    parsedQueryPaginationModel && parsedQueryPaginationModel.pageSize <= 100 && parsedQueryPaginationModel.page >= 0
      ? parsedQueryPaginationModel
      : {
          page: 0,
          pageSize: 50,
        }
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(
    querySortModel ? (JSON.parse(querySortModel) as GridSortModel) : [{ field: 'lastMessageDate', sort: 'desc' }]
  );
  const isInitialLoad = useRef(true);

  // ── Tab state (for ClientQueueTable) ──
  const querySelectedTab = queryParams.get('selectedTab');
  const [clientQueueSelectedTab, setClientQueueSelectedTab] = useState<ClientQueueTab>(() => {
    if (querySelectedTab != null) return Number(querySelectedTab) as ClientQueueTab;
    // Advanced filters (brand, branch office, assigned user…) are cross-cutting searches; the
    // default "Sin atender" queue is a small triage bucket, so ANDing them there can blank an
    // otherwise-populated result. Land on "Todos" whenever a smart filter is already active.
    return smartFilterItems.length > 0 ? ClientQueueTab.ALL : ClientQueueTab.ASSIGNED;
  });
  const [tabFilterItems, setTabFilterItems] = useState<GridFilterItem[]>([]);
  // Tab-specific count buckets emitted by ClientQueueTable (composed below with base/external filters).
  const [countBuckets, setCountBuckets] = useState<ClientCountBucket[]>([]);

  // Same rationale: when the user applies a smart filter on a narrow tab, jump to "Todos" so
  // the filter searches the whole client base instead of intersecting with the current queue.
  const prevSmartFilterCount = useRef(smartFilterItems.length);
  useEffect(() => {
    if (smartFilterItems.length > 0 && prevSmartFilterCount.current === 0) {
      setClientQueueSelectedTab(ClientQueueTab.ALL);
    }
    prevSmartFilterCount.current = smartFilterItems.length;
  }, [smartFilterItems.length]);

  // Merge bar + SmartFilterModal + tab filter items into the DataGrid's filterModel
  const mergedFilterModel: GridFilterModel = useMemo(
    () => ({
      ...filterModel,
      items: [...filterModel.items, ...barFilterItems, ...smartFilterItems, ...tabFilterItems],
    }),
    [filterModel, barFilterItems, smartFilterItems, tabFilterItems]
  );

  // Compose each tab's count bucket with the same base + external filters the list applies, so every
  // badge total matches the rows its tab would render. Sent with the list query — one response, one
  // source of truth for the list and every badge.
  const composedCountBuckets = useMemo(
    () =>
      countBuckets.map((bucket) => ({
        key: bucket.key,
        items: [...filterModel.items, ...barFilterItems, ...smartFilterItems, ...bucket.items],
      })),
    [countBuckets, filterModel.items, barFilterItems, smartFilterItems]
  );

  const externalChips = useMemo(
    () => gridItemsToActiveFilters(smartFilterItems, clientsFilterFields, dynamicOptions),
    [smartFilterItems, dynamicOptions]
  );

  // Seed bar filter values from URL on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run at load time
  useEffect(() => {
    if (querySearchTerm) filter.setValue('searchTerm', querySearchTerm);
    if (querySelectedSources) filter.setValue('sources', JSON.parse(querySelectedSources) as string[]);
    if (querySelectedTopics) filter.setValue('topics', JSON.parse(querySelectedTopics) as string[]);
    if (queryStartDate || queryEndDate) {
      // Snap to full-day bounds so a bare `YYYY-MM-DD` deep-link end still includes the
      // closing date; already end-of-day ISO values are left unchanged (idempotent).
      filter.setValue('dateRange', {
        start: queryStartDate ? moment(queryStartDate).startOf('day') : null,
        end: queryEndDate ? moment(queryEndDate).endOf('day') : null,
      });
    }
  }, []);

  // Keep clients filters in sync when business unit changes from sidebar selector.
  // Also clamp URL/local BU when the user no longer has access (stale deep-link).
  useEffect(() => {
    const allowedBusinessUnitIds = businessUnits?.map((businessUnit) => businessUnit.id) ?? null;
    const resolvedBusinessUnitId =
      selectedBusinessUnitId !== null && selectedBusinessUnitId !== -1 ? selectedBusinessUnitId : businessUnitId;

    if (
      !currentUser?.isAdmin &&
      allowedBusinessUnitIds &&
      allowedBusinessUnitIds.length > 0 &&
      resolvedBusinessUnitId !== null &&
      !allowedBusinessUnitIds.includes(resolvedBusinessUnitId)
    ) {
      const fallbackBusinessUnitId = allowedBusinessUnitIds[0];
      if (fallbackBusinessUnitId !== businessUnitId) {
        setBusinessUnitId(fallbackBusinessUnitId);
        setTabFilterItems([]);
        setClientQueueSelectedTab(ClientQueueTab.ASSIGNED);
      }
      return;
    }

    if (selectedBusinessUnitId === null || selectedBusinessUnitId === -1 || selectedBusinessUnitId === businessUnitId) {
      return;
    }
    setBusinessUnitId(selectedBusinessUnitId);
    setTabFilterItems([]);
    setClientQueueSelectedTab(ClientQueueTab.ASSIGNED);
  }, [selectedBusinessUnitId, businessUnitId, businessUnits, currentUser?.isAdmin]);
  const lacksBusinessUnitAccess =
    !currentUser?.isAdmin &&
    businessUnitId != null &&
    !!businessUnits &&
    businessUnits.length > 0 &&
    !businessUnits.some((businessUnit) => businessUnit.id === businessUnitId);

  const { data, refetch, isFetching } = useGetClientsQuery(
    {
      paginationModel,
      filterModel: mergedFilterModel,
      sortModel,
      searchTerm,
      businessUnitId: businessUnitId || undefined,
      businessUnitType: businessUnitTypeFilter,
      countBuckets: composedCountBuckets,
    } as ClientsQueryParams,
    { skip: (currentUser?.isAdmin === true && !businessUnitId) || lacksBusinessUnitAccess }
  );

  const { clients, total, counts: tabCounts } = data || { clients: [], total: 0, counts: {} };

  let mostDominantBusinessUnitId: number | null = businessUnitId;

  if (!mostDominantBusinessUnitId && clients.length > 5) {
    // if all the clients have the same business unit id, subscribe to the business unit id
    const firstBusinessUnitId = clients[0].businessUnitId;
    if (clients.every((client) => client.businessUnitId === firstBusinessUnitId)) {
      mostDominantBusinessUnitId = firstBusinessUnitId;
    }
  }

  const businessUnitType =
    new Set(clients.map((client) => client.businessUnit?.type)).size === 1 ? clients[0].businessUnit?.type : '';

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    const businessUnitsWithClientRefresh = [
      SALFA_RAC_EXT_BUSINESS_UNIT_ID,
      SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID,
      SALFA_USED_ID,
    ];
    if (
      ['seller', 'sellerWithAssignment'].includes(currentUser?.role || '') &&
      mostDominantBusinessUnitId &&
      businessUnitsWithClientRefresh.includes(mostDominantBusinessUnitId)
    ) {
      const channelName = `business-unit-message-${mostDominantBusinessUnitId}`;
      const { channel, unsubscribe } = subscribeChannel(channelName);
      const handler = () => {
        refetchRef.current().catch(() => {
          console.error('Error refetching clients triggered by new message');
        });
      };
      channel.bind('new-message', handler);
      return () => {
        channel.unbind('new-message', handler);
        unsubscribe();
      };
    }
    if (mostDominantBusinessUnitId) {
      const channelName = `business-unit-message-${mostDominantBusinessUnitId}`;
      const { channel, unsubscribe } = subscribeChannel(channelName);
      const handler = (event: NewMessageEvent) => {
        setNewEvents((prev) => [...prev, event]);
      };
      channel.bind('new-message', handler);
      return () => {
        channel.unbind('new-message', handler);
        unsubscribe();
      };
    }
    return () => {};
  }, [mostDominantBusinessUnitId, currentUser?.role]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // mergedFilterModel is intentionally used as a trigger only
  }, []);

  useEffect(() => {
    if (currentUser?.isAdmin && !businessUnitId) return;
    refetch().catch(() => {
      console.error('Error fetching clients');
    });
  }, [businessUnitId, refetch, currentUser?.isAdmin]);

  useEffect(() => {
    // Sync all filter state to URL query params in a single update
    const newParams = new URLSearchParams();

    if (filterModel.items.length > 0) {
      newParams.set('filterModel', JSON.stringify(filterModel));
    }
    newParams.set('paginationModel', JSON.stringify(paginationModel));
    if (sortModel.length > 0) {
      newParams.set('sortModel', JSON.stringify(sortModel));
    }
    newParams.set('searchTerm', searchTerm);
    newParams.set('businessUnitId', businessUnitId?.toString() || '');
    newParams.set('businessUnitType', businessUnitTypeFilter || '');
    if (smartFilterItems.length > 0) {
      newParams.set('smartFilterItems', JSON.stringify(smartFilterItems));
    }
    if (selectedSources.length > 0) {
      newParams.set('selectedSources', JSON.stringify(selectedSources));
    }
    if (selectedTopics.length > 0) {
      newParams.set('selectedTopics', JSON.stringify(selectedTopics));
    }
    if (startDate) {
      newParams.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      newParams.set('endDate', endDate.toISOString());
    }
    newParams.set('selectedTab', clientQueueSelectedTab.toString());

    navigate(`?${newParams.toString()}`, { replace: true });
  }, [
    navigate,
    filterModel,
    paginationModel,
    sortModel,
    searchTerm,
    businessUnitId,
    smartFilterItems,
    selectedSources,
    selectedTopics,
    startDate,
    endDate,
    clientQueueSelectedTab,
  ]);

  if (messaging) {
    onMessage(messaging, (payload) => {
      console.log('Message received from clients page:', payload);
      if (
        payload.data?.identifier === 'new-client-assigned' ||
        payload.data?.identifier === 'new-client-message' ||
        payload.data?.identifier === 'client-auto-released'
      ) {
        const notificationAudio = new Audio('/notification-sound.wav');
        notificationAudio.play().catch((e) => {
          console.error(e);
        });
        refetch().catch((e) => {
          console.error(e);
        });
      }
    });
  }

  const navigateToClientId = (id: number, newPage = false) => {
    queryParams.set('highlightedClientId', id.toString());
    if (newPage) {
      window.open(`/clients/${id}`, '_blank');
      return;
    }
    navigate(`?${queryParams.toString()}`, { replace: true });

    // Pass current clients list to the client page via navigation state
    navigate(`/clients/${id}?${queryParams.toString()}`, {
      state: {
        clientsList: clients,
        from: `/clients?${queryParams.toString()}`,
      },
    });
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: SURFACE }}>
      <Box sx={{ pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2 }, px: { xs: 1, md: 2 }, flexShrink: 0 }}>
        <MasterFilter
          {...filter}
          dynamicOptions={dynamicOptions}
          onAdvancedClick={() => setSmartFilterOpen(true)}
          advancedFilterCount={smartFilterItems.length}
          externalChips={externalChips}
          onRemoveExternalChip={(key) => setSmartFilterItems((prev) => prev.filter((i) => i.field !== key))}
          onClearAllExternalChips={() => setSmartFilterItems([])}
        />
        <SmartFilterModal
          open={isSmartFilterOpen}
          onClose={() => setSmartFilterOpen(false)}
          fieldDefs={clientsFilterFields}
          dynamicOptions={dynamicOptions}
          initialItems={smartFilterItems}
          onApply={(items) => {
            setSmartFilterItems(items);
            setSmartFilterOpen(false);
          }}
          context={{ businessUnitType: businessUnitType || null, businessUnitId }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: { xs: 1, md: 2 }, py: 1 }}>
        {currentUser?.isAdmin && !businessUnitId ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: `${NAVY}99`,
              }}
            >
              Selecciona una unidad de negocio para ver los clientes
            </Typography>
          </Box>
        ) : (
          <ClientsTable
            clients={clients}
            isLoading={isFetching}
            refetch={() => {
              setNewEvents([]);
              refetch().catch(() => {
                console.error('Error fetching clients');
              });
            }}
            filterModel={mergedFilterModel}
            paginationModel={paginationModel}
            sortModel={sortModel}
            setFilterModel={setFilterModel}
            setPaginationModel={setPaginationModel}
            setSortModel={setSortModel}
            navigateToClientId={navigateToClientId}
            highlightedClientId={highlightedClientId}
            total={total}
            businessUnitType={businessUnitType || ''}
            newEvents={newEvents}
            startDate={startDate}
            endDate={endDate}
            clientQueueSelectedTab={clientQueueSelectedTab}
            setClientQueueSelectedTab={setClientQueueSelectedTab}
            setTabFilterItems={setTabFilterItems}
            setCountBuckets={setCountBuckets}
            tabCounts={tabCounts}
          />
        )}
      </Box>
    </div>
  );
};

export default Clients;
